// ThemeSwitcher — botón flotante para alternar entre las paletas A / B / C.
//
// Uso temporal mientras se define el look definitivo del sitio. Guarda la
// selección en localStorage y aplica document.documentElement.dataset.theme
// para que globals.css pueda hacer override de las variables de color.
//
// Cuando el cliente decida el tema, este componente se retira y se deja
// el bloque data-theme correspondiente activado en globals.css o
// directamente se colapsan sus valores en :root.

import { useEffect, useState, useRef } from 'react';
import { Settings, Check } from 'lucide-react';
import styles from './ThemeSwitcher.module.css';

const THEMES = [
  {
    id: 'a',
    name: 'Fenagro cooperativo',
    subtitle: 'Verde institucional + oro',
    swatches: ['#1a3a2a', '#c9a227', '#fafaf7'],
  },
  {
    id: 'b',
    name: 'Editorial oro',
    subtitle: 'Negro tipográfico + oro',
    swatches: ['#111111', '#c9a227', '#ffffff'],
  },
  {
    id: 'c',
    name: 'Bicolor institucional',
    subtitle: 'Verde + amarillo cálido',
    swatches: ['#1a3a2a', '#eab308', '#ffffff'],
  },
];

const STORAGE_KEY = 'news:theme';

export default function ThemeSwitcher({ variant = 'floating', panelUp = false, triggerClassName, iconSize, label = 'Tema' }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => {
    if (typeof window === 'undefined') return 'a';
    return localStorage.getItem(STORAGE_KEY) || 'a';
  });
  const panelRef = useRef(null);

  // Aplicar el tema al montar y cuando cambia
  useEffect(() => {
    document.documentElement.dataset.theme = active;
    localStorage.setItem(STORAGE_KEY, active);
  }, [active]);

  // Cerrar el panel al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const rootCls = variant === 'inline'
    ? `${styles.rootInline} ${triggerClassName ? styles.rootFullWidth : ''}`.trim()
    : styles.root;
  const triggerCls = triggerClassName || (variant === 'inline' ? styles.triggerInline : styles.trigger);
  const effectiveIconSize = iconSize ?? (variant === 'inline' ? 14 : 20);
  const panelCls = `${styles.panel} ${panelUp ? styles.panelUp : ''}`.trim();

  return (
    <div className={rootCls} ref={panelRef}>
      <button
        type="button"
        className={triggerCls}
        onClick={() => setOpen(o => !o)}
        aria-label="Cambiar tema visual del sitio"
        title="Cambiar tema visual del sitio"
      >
        <Settings size={effectiveIconSize} />
        {variant === 'inline' && <span>{label}</span>}
      </button>

      {open && (
        <div className={panelCls} role="dialog" aria-label="Selector de tema">
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>Tema visual</span>
            <span className={styles.panelHint}>Previa en vivo</span>
          </div>
          <ul className={styles.list}>
            {THEMES.map(t => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`${styles.item} ${active === t.id ? styles.itemActive : ''}`}
                  onClick={() => setActive(t.id)}
                >
                  <div className={styles.swatches} aria-hidden="true">
                    {t.swatches.map((s, i) => (
                      <span key={i} className={styles.swatch} style={{ background: s }} />
                    ))}
                  </div>
                  <div className={styles.itemText}>
                    <span className={styles.itemName}>{t.name}</span>
                    <span className={styles.itemSub}>{t.subtitle}</span>
                  </div>
                  {active === t.id && (
                    <Check size={16} className={styles.itemCheck} aria-hidden="true" />
                  )}
                </button>
              </li>
            ))}
          </ul>
          <p className={styles.footnote}>
            Prototipo de decisión. La selección solo aplica en este navegador.
          </p>
        </div>
      )}
    </div>
  );
}
